import { Link } from 'react-router-dom';
import React, { useEffect, useState, useMemo } from 'react';

import { Avatar } from '@vyaguta/vyaguta-ui';
import { UiDownloadAlt } from '@vyaguta/vyaguta-icons/ui';

import LFModal from 'components/common/LFModal';
import Empty from 'components/common/empty/Empty';
import { EMPLOYEES } from 'constants/permissions';
import Loading from 'components/common/loading/Loading';
import { SkillCategoryChild } from 'components/common/skills';

import * as routes from 'constants/routes';

import history from 'utils/history';
import { handleError } from 'utils/errorHandler';
import { parseQueryURL } from 'utils/queryParam';
import { getEmpImageUrl, interpolate, pluralize, stringToArray } from 'utils/string';
import { getEndSkillIds, getSearchAndRemainingSkills, getSearchTreeWithRating } from 'utils/skillV2';

import * as skillService from 'services/skillService';
import { useUserPermissions } from 'hooks/query/useUserInfo';

import type { SkillNode, SearchableSkill, PeopleFinderEmployeeRow, ResourceFinderInformationSlice } from '../types';

const viewAllSkills = (onClick: () => void, length: number): React.ReactElement => (
  <button type="button" className="view-all font-size-14" onClick={onClick}>
    View {length} others
  </button>
);

const parseSkillsFrom = (
  skills: SearchableSkill[] | null | undefined,
  skillsTree: SkillNode[],
  remainingLength: number | undefined
): React.ReactNode =>
  skills && skills.length ? (
    <>
      {skills.map((skill) => (
        <div className="skill-pill-item d-flex align-items-center p-12" key={String(skill.id)}>
          <span>{String(skill.name ?? '')}</span>

          {skill.rating ? (
            <div className="rating d-flex align-items-center justify-content-center mlpx-6">
              <span className="font-size-12 color-grey-60">{String(skill.rating)}</span>
            </div>
          ) : null}
        </div>
      ))}

      {!!remainingLength && (
        <LFModal
          closeButton={true}
          TriggerBtn={(triggerProps) => viewAllSkills(triggerProps.onClick, remainingLength)}
          title={{ text: 'Selected Skills', type: 'info' }}
          htmlContent={
            <div className="skill-set-area__container p-0">
              <div className="skills-list-container p-0 w-100p">
                {skillsTree.map((skill) => (
                  <SkillCategoryChild
                    key={String(skill.id)}
                    showAddSkills={false}
                    openEditCategoryModal={() => undefined}
                    openAddSkillModal={() => undefined}
                    openEditSkillModal={() => undefined}
                    selectedCategory={skill}
                    skillListClass="height-auto mbpx-16"
                  />
                ))}
              </div>
            </div>
          }
          renderFooter={false}
          className="people-finder-view-all-modal container"
        />
      )}
    </>
  ) : null;

interface EmployeeRowProps {
  employee: PeopleFinderEmployeeRow;
  allSkills: SkillNode[];
  skillsQuery: string[];
  endSkillIds: Set<string | number>;
}

const EmployeeRow: React.FC<EmployeeRowProps> = ({
  employee: { user, skills },
  allSkills,
  skillsQuery,
  endSkillIds,
}) => {
  const { id, empId, fullname, designation } = user;

  const [searchSkills, remainingSkills] = useMemo(
    () => getSearchAndRemainingSkills(skills, skillsQuery),
    [skills, skillsQuery]
  );

  const remainingEndSkills = useMemo(
    () => remainingSkills.filter((skill) => endSkillIds.has(skill.id)),
    [remainingSkills, endSkillIds]
  );

  const skillsTree = useMemo(
    () => getSearchTreeWithRating(remainingEndSkills, allSkills),
    [remainingEndSkills, allSkills]
  );

  return (
    <div className="d-flex align-items-center border-bottom-grey-10 p-y-16">
      <div className="d-flex align-items-center w-60x">
        <Avatar
          className="nametag nametag--lg profile-photo people-finder-profile"
          image={getEmpImageUrl(String(empId))}
          alt={fullname}
        />

        <div className="namecard__info">
          <Link className="profile-link text--bold text--grey fs-13" to={interpolate(routes.EMPLOYEE_PROFILE, { id })}>
            {fullname}
          </Link>
          <div className="muted-text res-designation-name">{designation.name}</div>
        </div>
      </div>

      <div className="skills-pill-list d-flex gap-px-16 flex-wrap">
        {parseSkillsFrom(searchSkills, skillsTree, remainingEndSkills?.length)}
      </div>
    </div>
  );
};

export interface ResourceResultsProps {
  total: number;
  employees: PeopleFinderEmployeeRow[];
  exporting: boolean;
  onExport: () => void | Promise<void>;
}

const ResourceResults: React.FC<ResourceResultsProps> = ({ total, employees, exporting, onExport }) => {
  const permissions = useUserPermissions() as ResourceFinderInformationSlice['information']['value']['permissions'];

  const { skill: skillsQueryRaw } = parseQueryURL(history.location.search);

  let skillsQueryList: string[] = [];
  if (typeof skillsQueryRaw === 'string' && skillsQueryRaw) {
    const parsedSkillIds = stringToArray(skillsQueryRaw, ',', true);
    skillsQueryList = (Array.isArray(parsedSkillIds) ? parsedSkillIds : []).map(String).filter(Boolean);
  } else if (Array.isArray(skillsQueryRaw)) {
    skillsQueryList = skillsQueryRaw.map(String).filter(Boolean);
  }

  const [allSkills, setAllSkills] = useState<SkillNode[]>([]);

  const endSkillIds = useMemo(() => getEndSkillIds(allSkills), [allSkills]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await skillService.fetchAll();

        setAllSkills(data as SkillNode[]);
      } catch (err) {
        handleError(err as object);
      }
    })();
  }, []);

  return (
    <>
      <div className="resource-list-header table-header name-wrap border-bottom-grey-20">
        <div>
          <p className="resource-header-title">Leapfroggers</p>

          <p className="font-size-12 color-grey-60">
            {employees.length ? `Showing ${employees.length} Leapfroggers` : ''}
          </p>
        </div>

        <div className="d-flex flex-row ai-center">
          <span className="resource-results">{pluralize(`${total || 'No'} Result`, total)}</span>
          {permissions[EMPLOYEES.EXPORT] && !!total && (
            <button
              type="button"
              className="btn btn--outlined-grey export-button"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? (
                <div className="export-button loading">
                  <Loading />
                </div>
              ) : (
                <>
                  <UiDownloadAlt className="download-icon" />
                  {'.CSV'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="resource-result-list">
        {employees && employees.length ? (
          employees.map((employee, index) => (
            <EmployeeRow
              employee={employee}
              key={index}
              allSkills={allSkills}
              skillsQuery={skillsQueryList}
              endSkillIds={endSkillIds}
            />
          ))
        ) : (
          <div className="empty-container__wrapper">
            <Empty message={'No leapfroggers found'} />
          </div>
        )}
      </div>
    </>
  );
};

export default ResourceResults;
